import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Lock, Mail, User, Loader2, Shield, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/components/ui/sonner';

export default function LoginPage() {
  const { login, setup, setupRequired, loading: authLoading } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' or 'setup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (setupRequired || mode === 'setup') {
        await setup(email, password, name);
        toast.success('Admin account created successfully!');
      } else {
        await login(email, password);
        toast.success('Welcome back!');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
      </div>
    );
  }

  const isSetupMode = setupRequired || mode === 'setup';

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Logo */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-zinc-100 font-['Manrope']">AcquiFlow</h1>
          </div>
          <p className="text-sm text-zinc-500">Business Acquisition Analysis Tool</p>
        </div>

        {/* Setup Notice */}
        {setupRequired && (
          <Card className="bg-indigo-500/10 border-indigo-500/30">
            <CardContent className="p-4 text-center">
              <Badge className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30 mb-2">First Time Setup</Badge>
              <p className="text-sm text-zinc-300">Create your admin account to get started</p>
            </CardContent>
          </Card>
        )}

        {/* Login/Setup Card */}
        <Card className="bg-zinc-900/60 border-zinc-800/60">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-medium text-zinc-100 text-center">
              {isSetupMode ? 'Create Admin Account' : 'Sign In'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name field (setup only) */}
              {isSetupMode && (
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="John Smith"
                      required={isSetupMode}
                      className="w-full bg-zinc-800/60 border border-zinc-700 rounded-lg text-sm text-zinc-200 py-2.5 pl-10 pr-4 focus:border-indigo-500/50 focus:outline-none focus:ring-1 focus:ring-indigo-500/20"
                      data-testid="name-input"
                    />
                  </div>
                </div>
              )}

              {/* Email */}
              <div>
                <label className="block text-xs text-zinc-400 mb-1.5">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    required
                    className="w-full bg-zinc-800/60 border border-zinc-700 rounded-lg text-sm text-zinc-200 py-2.5 pl-10 pr-4 focus:border-indigo-500/50 focus:outline-none focus:ring-1 focus:ring-indigo-500/20"
                    data-testid="email-input"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs text-zinc-400 mb-1.5">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    className="w-full bg-zinc-800/60 border border-zinc-700 rounded-lg text-sm text-zinc-200 py-2.5 pl-10 pr-10 focus:border-indigo-500/50 focus:outline-none focus:ring-1 focus:ring-indigo-500/20"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-2.5"
                data-testid="submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isSetupMode ? (
                  'Create Account & Sign In'
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-zinc-600">
          Secure business acquisition analysis platform
        </p>
      </div>
    </div>
  );
}
